import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, MapPin, Users, CalendarDays, Sparkles, ArrowRight, Snowflake, Sun } from "lucide-react";
import { useCMS, usePage, useSection } from "@/hooks/useCMS";
import { useLang } from "@/context/LanguageContext";
import { Eyebrow, StaggeredHeading, PillButton, ArrowImage, Reveal, TiltCard } from "@/components/motifs";
import { CmsSection } from "@/components/CmsSection";
import { TemplateSections } from "@/components/TemplateSections";
import { EventCard, RsvpModal } from "@/components/cards";
import { EventListingForm } from "@/components/EventListingForm";
import { isFutureEvent } from "@/lib/eventDate";

const TILTS = ["lg:-rotate-1", "lg:rotate-1", "lg:-rotate-2"];
const MTS = ["lg:mt-0", "lg:mt-8", "lg:mt-4"];

export default function Events() {
  const { data } = useCMS();
  const { t } = useLang();
  const page = usePage("events");
  const events = data.events || [];
  const featured = events.find((e) => e.status === "featured") || events[0] || {};
  const directory = events.filter((e) => e.id !== featured.id);
  const featuredPast = !isFutureEvent(featured);
  const campaigns = data.campaigns || [];
  const [filter, setFilter] = useState("all");
  const [rsvp, setRsvp] = useState(false);

  const FILTERS = [
    { key: "all", label: t("All", "Tous") },
    { key: "in-person", label: t("In-person", "En personne") },
    { key: "virtual", label: t("Virtual", "Virtuel") },
  ];

  const shown = directory.filter((e) => filter === "all" || e.format === filter);
  const secDir = useSection("ev_directory");

  return (
    <div data-testid="events-page" className="flex flex-col">
      {/* HERO */}
      <section className="mx-auto max-w-7xl px-4 sm:px-8 pt-14 pb-10 lg:pt-20">
        <div className="grid gap-12 lg:grid-cols-[5fr_7fr] lg:items-center">
          <div>
            <Eyebrow icon={CalendarDays}>{page.eyebrow || "Events across Canada"}</Eyebrow>
            <div className="mt-5">
              <StaggeredHeading animate lines={[{ text: page.headline_1 || "Find your" }, { text: page.headline_2 || "move.", emphasis: true, accent: "#DB1A5C" }]} />
            </div>
            <p className="mt-6 max-w-md text-lg text-muted-foreground">{page.intro}</p>
          </div>

          {/* Featured Cycle Power Hour */}
          <Reveal delay={0.1}>
            <TiltCard accent={featured.accent || "#DB1A5C"} className="overflow-hidden" testid="featured-event">
              <div className="relative h-56 overflow-hidden">
                <img src={featured.image} alt={featured.image_alt || featured.title} className="h-full w-full object-cover" />
                <span className="absolute top-4 left-4 rounded-full bg-[#DB1A5C] px-4 py-1.5 text-xs font-black uppercase text-white shadow">
                  Launch event · {featured.date}
                </span>
              </div>
              <div className="p-7">
                <h2 className="font-display font-black uppercase text-2xl sm:text-3xl">{featured.title}</h2>
                <div className="mt-4 grid gap-2 text-sm text-muted-foreground sm:grid-cols-2">
                  <p className="flex items-center gap-2"><Clock size={15} /> {featured.time}</p>
                  <p className="flex items-center gap-2"><MapPin size={15} /> {featured.location}</p>
                  <p className="flex items-center gap-2"><Users size={15} /> {featured.audience}</p>
                </div>
                <p className="mt-4 text-foreground/70">{featured.blurb}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <PillButton onClick={() => { if (!featuredPast) setRsvp(true); }} icon={ArrowRight} testid="featured-save">{featuredPast ? t("Registration closed", "Inscriptions fermées") : (featured.cta_primary_label || t("Save my spot", "Réserver ma place"))}</PillButton>
                  <PillButton to={featured.cta_secondary_link || "/Home#list"} variant="ghost" testid="featured-satellite">{featured.cta_secondary_label || t("Add a satellite ride", "Ajouter une randonnée")}</PillButton>
                </div>
              </div>
            </TiltCard>
          </Reveal>
        </div>
      </section>

      {/* DIRECTORY */}
      <CmsSection sk="ev_directory">
      <section id="directory" className="mx-auto max-w-7xl px-4 sm:px-8 py-14">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <Eyebrow icon={Sparkles}>{secDir.eyebrow || t("Upcoming everywhere", "À venir partout")}</Eyebrow>
            <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-4xl">{secDir.heading || t("Every move, one map", "Chaque mouvement, une carte")}</h2>
          </div>
          <div className="inline-flex rounded-full bg-muted p-1.5" role="tablist" aria-label="Filter events">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                role="tab"
                aria-selected={filter === f.key}
                onClick={() => setFilter(f.key)}
                className={`relative rounded-full px-5 py-2.5 text-sm font-bold transition-colors cursor-pointer ${filter === f.key ? "text-white" : "text-foreground/70 hover:text-foreground"}`}
                data-testid={`event-filter-${f.key}`}
              >
                {filter === f.key && (
                  <motion.span layoutId="filter-pill" className="absolute inset-0 rounded-full bg-[#0F172A]" transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                )}
                <span className="relative z-10">{f.label}</span>
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          <AnimatePresence mode="popLayout">
            {shown.map((ev, i) => (
              <motion.div
                key={ev.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                <EventCard ev={ev} tilt={TILTS[i % 3]} mt={MTS[i % 3]} testid={`event-card-${ev.slug || i}`} />
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
        {shown.length === 0 && <p className="mt-10 text-center text-muted-foreground">{t("No events in this category yet.", "Aucun événement dans cette catégorie pour l'instant.")}</p>}
      </section>

      </CmsSection>
      {/* SEASONS */}
      <CmsSection sk="ev_seasons">
      <section id="seasons" className="bg-[#2CB6C2]/10 py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8">
          <Reveal>
            <Eyebrow icon={Snowflake}>{t("Two national moments", "Deux moments nationaux")}</Eyebrow>
            <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95] max-w-2xl">
              {t("Two moments that move", "Deux moments qui font bouger")} <span className="italic text-[#2CACD4]">{t("the whole country.", "tout le pays.")}</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-8 md:grid-cols-2">
            {campaigns.map((c, i) => {
              const Icon = c.icon === "Sun" ? Sun : Snowflake;
              return (
                <Reveal key={c.id || i} delay={i * 0.1}>
                  <TiltCard accent={c.accent} tilt={i === 0 ? "lg:-rotate-1" : "lg:rotate-1"} className="overflow-hidden h-full" testid={`season-${i}`}>
                    <div className="relative h-56 overflow-hidden">
                      <img src={c.image} alt={c.image_alt || c.name} className="h-full w-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <span className="absolute bottom-4 left-5 flex items-center gap-2 text-white font-black uppercase text-sm" style={{ color: c.accent }}>
                        <Icon size={18} /> {c.timing}
                      </span>
                    </div>
                    <div className="p-7">
                      <h3 className="font-display font-black uppercase text-2xl">{c.name}</h3>
                      <p className="mt-3 text-muted-foreground">{c.blurb}</p>
                      <div className="mt-5">
                        <PillButton to={c.cta_link || "#directory"} icon={ArrowRight} testid={`season-cta-${i}`}>{c.cta_label || "See events"}</PillButton>
                      </div>
                    </div>
                  </TiltCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      </CmsSection>
      {/* DARK HOST */}
      <CmsSection sk="ev_host">
      <section id="list" className="bg-[#0F172A] text-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <Eyebrow icon={CalendarDays} color="#CAE295">{t("Host an event", "Organiser un événement")}</Eyebrow>
            <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
              {t("Hosting something?", "Vous organisez?")} <span className="italic text-[#2CACD4]">{t("Listing is free.", "L'inscription est gratuite.")}</span>
            </h2>
            <p className="mt-5 text-white/70 max-w-md">{t("Add your community move to the national directory. Approved listings go live within days.", "Ajoutez votre activité au répertoire national. Les inscriptions approuvées sont publiées en quelques jours.")}</p>
          </div>
          <EventListingForm />
        </div>
      </section>
      </CmsSection>

      {rsvp && <RsvpModal ev={featured} onClose={() => setRsvp(false)} />}
      <TemplateSections page="events" />
    </div>
  );
}
